<template>
  <div class="min-h-screen flex items-center justify-center p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-yellow/10 rounded-full blur-3xl"
      ></div>
    </div>

    <!-- Register card -->
    <div class="relative w-full max-w-md">
      <div class="card p-8">
        <!-- Logo -->
        <div class="text-center mb-8">
          <div
            class="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 via-accent-red to-accent-yellow mb-4 shadow-lg shadow-primary-500/25"
          >
            <span class="text-white font-bold text-3xl font-display">P</span>
          </div>
          <h1 class="font-display text-3xl font-bold text-gradient">pushx.pro</h1>
          <p class="text-gray-400 mt-2">Создайте новый аккаунт</p>
        </div>

        <!-- Referral badge -->
        <div
          v-if="form.referral_code"
          class="mb-6 p-4 rounded-lg bg-primary-500/10 border border-primary-500/30"
        >
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p class="text-sm text-primary-400">
              Вы регистрируетесь по приглашению (код: <span class="font-mono font-semibold">{{ form.referral_code }}</span>)
            </p>
          </div>
        </div>

        <!-- Error message -->
        <div
          v-if="errorMessage"
          class="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <p class="text-sm text-red-400">{{ errorMessage }}</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleRegister" class="space-y-5">
          <div>
            <label class="form-label">Имя</label>
            <input
              v-model="form.name"
              type="text"
              class="input-field"
              placeholder="Введите ваше имя"
              required
              autocomplete="name"
            />
            <div v-if="errors.name" class="mt-1 text-sm text-red-400">
              {{ errors.name }}
            </div>
          </div>

          <div>
            <label class="form-label">Email</label>
            <input
              v-model="form.email"
              type="email"
              class="input-field"
              placeholder="email@example.com"
              required
              autocomplete="email"
            />
            <div v-if="errors.email" class="mt-1 text-sm text-red-400">
              {{ errors.email }}
            </div>
          </div>

          <div>
            <label class="form-label">Пароль</label>
            <div class="relative">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="input-field pr-10"
                placeholder="••••••••"
                required
                autocomplete="new-password"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
              >
                <svg
                  v-if="showPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
            <div v-if="errors.password" class="mt-1 text-sm text-red-400">
              {{ errors.password }}
            </div>
          </div>

          <div>
            <label class="form-label">Подтвердите пароль</label>
            <div class="relative">
              <input
                v-model="form.password_confirmation"
                :type="showPasswordConfirm ? 'text' : 'password'"
                class="input-field pr-10"
                placeholder="••••••••"
                required
                autocomplete="new-password"
              />
              <button
                type="button"
                @click="showPasswordConfirm = !showPasswordConfirm"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
              >
                <svg
                  v-if="showPasswordConfirm"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
            <div v-if="errors.password_confirmation" class="mt-1 text-sm text-red-400">
              {{ errors.password_confirmation }}
            </div>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base font-semibold"
          >
            <svg
              v-if="loading"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ loading ? 'Регистрация...' : 'Зарегистрироваться' }}
          </button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-sm text-gray-500">
            Уже есть аккаунт?
            <button
              type="button"
              @click="$router.push('/login')"
              class="font-medium text-primary-500 hover:text-primary-400 transition-colors duration-200 ml-1"
            >
              Войти
            </button>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/auth';
import { useNotifications } from '../composables/useNotifications';

export default {
  name: 'Register',
  setup() {
    const authStore = useAuthStore();
    const { success, error } = useNotifications();

    return {
      authStore,
      success,
      error,
    };
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        referral_code: '',
      },
      showPassword: false,
      showPasswordConfirm: false,
      errors: {},
      loading: false,
      errorMessage: '',
    };
  },
  created() {
    // Получаем реферальный код из URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      this.form.referral_code = refCode;
      // Сохраняем в localStorage на случай перезагрузки страницы
      localStorage.setItem('referral_code', refCode);
    } else {
      // Пробуем восстановить из localStorage
      const savedCode = localStorage.getItem('referral_code');
      if (savedCode) {
        this.form.referral_code = savedCode;
      }
    }
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (!this.form.name) {
        this.errors.name = 'Введите имя';
        return false;
      }

      if (this.form.name.length < 2) {
        this.errors.name = 'Имя должно содержать минимум 2 символа';
        return false;
      }

      if (!this.form.email) {
        this.errors.email = 'Введите email';
        return false;
      }

      if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Введите корректный email';
        return false;
      }

      if (!this.form.password) {
        this.errors.password = 'Введите пароль';
        return false;
      }

      if (this.form.password.length < 8) {
        this.errors.password = 'Пароль должен содержать минимум 8 символов';
        return false;
      }

      if (!this.isValidPassword(this.form.password)) {
        this.errors.password =
          'Пароль должен содержать минимум одну строчную букву, одну заглавную букву и одну цифру';
        return false;
      }

      if (!this.form.password_confirmation) {
        this.errors.password_confirmation = 'Подтвердите пароль';
        return false;
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.errors.password_confirmation = 'Пароли не совпадают';
        return false;
      }

      return true;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidPassword(password) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
      return passwordRegex.test(password);
    },

    async handleRegister() {
      this.errorMessage = '';
      
      if (!this.validateForm()) {
        return;
      }

      try {
        this.loading = true;

        await this.authStore.register(this.form);

        // Очищаем сохранённый реферальный код после успешной регистрации
        localStorage.removeItem('referral_code');

        this.success('Успех', 'Регистрация успешна! Добро пожаловать!');

        await this.$nextTick();
        this.$router.push('/dashboard');
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Ошибка регистрации';
        this.error('Ошибка', this.errorMessage);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
